import {ApolloProvider} from '@apollo/client';
import {maybeAddRequestMarkers} from '@stripe-internal/request-markers';
import {useMemo} from 'react';
import {createApolloClient} from 'src/internal/apollo/client';
import DataConfigContext from 'src/internal/config/DataConfigContext';
import KillSwitchProvider from 'src/internal/killswitch/KillSwitchProvider';
import DataRevampLayer from 'src/internal/revamp/DataRevampLayer';
import Headers from 'src/internal/shared/headers/headers';
import useOnChangedUser from 'src/internal/user/useOnChangedUser';
import addLanguageHeaders from 'src/public/headers/addLanguageHeaders';

import type {ReactElement, ReactNode} from 'react';
import type {DataConfig} from 'src/internal/config/types';
import type {FetchOptions, FetchUrl} from 'src/internal/shared/fetch/types';

type Props = {
  /** The application content to wrap with data context. */
  children: ReactNode;
  /** Configuration for data fetching, GraphQL, storage, and observability. */
  config: DataConfig;
};

function OnChangeLoggedUser({
  onChangeLoggedUser,
}: {
  onChangeLoggedUser: () => void;
}): ReactNode {
  useOnChangedUser(onChangeLoggedUser);
  return null;
}

/**
 * @see https://sail.stripe.me/data/api/DataProvider
 */
export default function DataProvider({children, config}: Props): ReactElement {
  const client = useMemo(
    () => (config.graphql ? createApolloClient(config.graphql) : null),
    [config.graphql],
  );

  // The `<DataProvider>` ensures that when calling the provided fetch, headers
  // are always passed as a record following the `FetchOptions` type. The
  // normalization is made by the `@sail/data` `Headers` class.
  const fetch = useMemo(() => {
    const fetch = config.fetch;

    if (!fetch) {
      return fetch;
    }

    return (url: FetchUrl, options?: FetchOptions): Promise<Response> => {
      const headers = new Headers(options?.headers ?? {});

      if (config.locale) {
        addLanguageHeaders(headers, config.locale);
      }

      const markerRes = maybeAddRequestMarkers(url, headers.getFetchHeaders());
      return fetch(markerRes.url, {
        ...options,
        headers: markerRes.headers,
      });
    };
  }, [config.fetch, config.locale]);

  const configWithFetch: DataConfig = useMemo(() => {
    return {
      ...config,
      fetch,
    };
  }, [config, fetch]);

  if (!client) {
    return (
      <DataConfigContext.Provider value={configWithFetch}>
        <KillSwitchProvider>{children}</KillSwitchProvider>
      </DataConfigContext.Provider>
    );
  }

  const handleChangedUser = config.onChangeLoggedUser;

  return (
    <DataConfigContext.Provider value={configWithFetch}>
      <KillSwitchProvider>
        <ApolloProvider client={client}>
          <DataRevampLayer
            config={config.unstable_sail_data_revamp}
            fetchFromOldProvider={fetch}
          >
            {children}
          </DataRevampLayer>
        </ApolloProvider>

        {handleChangedUser ? (
          <OnChangeLoggedUser onChangeLoggedUser={handleChangedUser} />
        ) : (
          <></>
        )}
      </KillSwitchProvider>
    </DataConfigContext.Provider>
  );
}
